import React, { useEffect, useRef } from "react";

/*
  Custom pointer, in the spirit of the Unseen Studio reference.

  What that reference actually does, read off its live stylesheet rather than
  guessed from a screenshot: a small rounded-square ring replaces the arrow
  (`.cursor__circle`, 2.2rem, 1.5px border, 1.2rem radius), and it changes state
  over things you can act on, growing and picking up a tiny uppercase label
  (`.cursor__click-hold-prompt > span > span`, .5rem, uppercase). The ring
  inverts its stroke on dark sections. It is contextual, not decorative: its job
  is to tell you what the thing under the pointer does.

  Ours is the same idea on our own tokens: a chrome ring on the 10px radius from
  the brand scale, gold when it is over a gold control, and a JetBrains Mono
  label at the eyebrow tracking.

  Three rules this obeys.

  1. It only exists where there is a real pointer. `(hover: hover) and
     (pointer: fine)` means a phone never renders it and loses nothing.
  2. The native cursor is only suppressed where the ring is a better answer.
     Over inputs and textareas the arrow and the caret come back, because a text
     caret carries information a ring does not.
  3. It ADDS to the resting state and never resolves one. Nothing on this page is
     hidden until you move the pointer at it. A visitor who never moves the mouse
     sees exactly the same content.
*/

// Follow speed. 1 is instant. Lower trails; too low and it feels like lag
// rather than weight.
const EASE = 0.22;

export default function PointerRing() {
  const ringRef = useRef(null);
  const labelRef = useRef(null);
  const state = useRef({ x: -100, y: -100, rx: -100, ry: -100, raf: 0, on: false });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const fine = window.matchMedia("(hover: hover) and (pointer: fine)");
    if (!fine.matches) return undefined;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const ring = ringRef.current;
    const label = labelRef.current;
    if (!ring) return undefined;

    const s = state.current;
    document.documentElement.classList.add("has-pointer-ring");

    const draw = () => {
      // Reduced motion gets the position with no easing: the ring is still
      // useful, it just does not trail.
      const k = reduced ? 1 : EASE;
      s.rx += (s.x - s.rx) * k;
      s.ry += (s.y - s.ry) * k;
      ring.style.transform = `translate3d(${s.rx}px, ${s.ry}px, 0) translate(-50%, -50%)`;
      s.raf = window.requestAnimationFrame(draw);
    };
    s.raf = window.requestAnimationFrame(draw);

    const move = (event) => {
      s.x = event.clientX;
      s.y = event.clientY;
      if (!s.on) {
        s.on = true;
        ring.classList.add("is-visible");
      }

      // What is under the pointer decides the ring's state. data-cursor carries
      // the label; anything interactive without one still gets the grown ring.
      const el = event.target instanceof Element ? event.target : null;
      const typed = el && el.closest("input, textarea, select, [contenteditable='true']");
      const hit = el && el.closest("[data-cursor], a, button");
      const gold = hit && (hit.classList.contains("button-gold") || hit.closest(".button-gold"));

      ring.classList.toggle("is-hidden", Boolean(typed));
      ring.classList.toggle("is-active", Boolean(hit) && !typed);
      ring.classList.toggle("is-gold", Boolean(gold) && !typed);

      const text = hit && !typed ? hit.getAttribute("data-cursor") : null;
      ring.classList.toggle("has-label", Boolean(text));
      if (label && label.textContent !== (text || "")) label.textContent = text || "";
    };

    const leave = () => {
      s.on = false;
      ring.classList.remove("is-visible");
    };
    const down = () => ring.classList.add("is-down");
    const up = () => ring.classList.remove("is-down");

    window.addEventListener("pointermove", move, { passive: true });
    window.addEventListener("pointerdown", down, { passive: true });
    window.addEventListener("pointerup", up, { passive: true });
    document.addEventListener("pointerleave", leave);

    return () => {
      window.cancelAnimationFrame(s.raf);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerdown", down);
      window.removeEventListener("pointerup", up);
      document.removeEventListener("pointerleave", leave);
      document.documentElement.classList.remove("has-pointer-ring");
    };
  }, []);

  return (
    <div className="pointer-ring" ref={ringRef} aria-hidden="true">
      <span className="pointer-ring-shape" />
      <span className="pointer-ring-label" ref={labelRef} />
    </div>
  );
}
